import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual } from 'react-redux';

import config from '../../../../config';
import defaultSearchCountry from '../../../../forms/HeroSearchForm/defaultSearchCountry';
import routeConfiguration from '../../../../routeConfiguration';
import { neutral } from '../../../../styles/mui/colors';
import useAppSelector from '../../../../util/hooks/useAppSelector';
import { hasLocationPermission, userLocation } from '../../../../util/maps';
import { FormattedMessage } from '../../../../util/reactIntl';
import { pathByRouteName } from '../../../../util/routes';
import Link from '../../../Links/Link/Link';
import NamedLink from '../../../Links/NamedLink/NamedLink';
import { locationBounds } from '../../../LocationAutocompleteInput/GeocoderMapbox';
import Logo from '../../../Logo/VanSite/Logo';
import LanguageMenu from './LanguageMenu';
import LoginMenu from './LoginMenu';
import ProfileMenu from './ProfileMenu';

const TopBarButtonComponent = ({
  to,
  messageId,
  color = 'inherit',
  variant = 'text',
  ariaLabel = 'Button Link',
  beta = false,
}) => {
  const intl = useIntl();
  const label = ariaLabel || intl.formatMessage({ id: messageId });

  return (
    <Button
      aria-label={label}
      color={color}
      component={Link}
      role="link"
      sx={{
        fontSize: 15,
        padding: 1.25,
      }}
      to={to}
      variant={variant}
    >
      <FormattedMessage id={messageId} />
      {beta ? (
        <Chip
          label="Beta"
          size="small"
          sx={{
            ml: 0.75,
            height: 18,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.5,
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      ) : null}
    </Button>
  );
};

TopBarButtonComponent.displayName = 'TopBarButton';
const TopBarButton = memo(TopBarButtonComponent);

const TopBarMenuButtonComponent = ({ messageId, items }) => {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const label = intl.formatMessage({ id: messageId });

  return (
    <>
      <Button
        aria-controls={open ? 'topbar-spots-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        color="secondary"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={handleOpen}
        sx={{
          fontSize: 15,
          padding: 1.25,
        }}
        variant="text"
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        id="topbar-spots-menu"
        keepMounted
        onClose={handleClose}
        open={open}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {items.map((item) => (
          <MenuItem component={Link} key={item.key} onClick={handleClose} to={item.to}>
            <FormattedMessage id={item.messageId} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

TopBarMenuButtonComponent.displayName = 'TopBarMenuButton';
const TopBarMenuButton = memo(TopBarMenuButtonComponent);

const logoStyle = {
  fontSize: '48px',
  p: 0,
  m: 0,
  width: 'auto',
  transition: 'transform 0.2s ease-in-out', // Smooth transition
  '&:hover': {
    transform: 'scale(0.95)', // Shrinks the logo to 90% of its size
  },
};

// The link strip plus the right-hand side needs about 1180 px, so below lg it would scroll the page
// sideways. The BottomNav's "Mehr" menu carries the same links there.
const navLinksSx = { display: { xs: 'none', lg: 'flex' } };

/**
 * TopbarDesktopComponent renders the desktop navigation bar for the application.
 * It includes a logo link, search form, localized navigation buttons, and user account menus.
 * The component adjusts based on user authentication state and page context, providing
 * an accessible and responsive design.
 *
 * Props:
 * - currentUserHasListings (boolean): Indicates if the current user has active listings.
 * - currentUser (object): The current authenticated user.
 * - currentPage (string): The name of the current page.
 * - isAuthenticated (boolean): Whether the user is authenticated.
 * - onLogout (function): Callback triggered on user logout.
 * - notificationCount (number): Number of notifications for the current user.
 */
const TopbarDesktopComponent = ({ currentPage = null, isAuthenticated, onLogout }) => {
  const intl = useIntl();
  const { currentUser, currentUserHasListings, currentUserNotificationCount } = useAppSelector(
    (state) => ({
      currentUser: state.user.currentUser,
      currentUserHasListings: state.user.currentUserHasListings,
      currentUserNotificationCount: state.user.currentUserNotificationCount,
    }),
    shallowEqual
  );
  /** @type {VanSiteTheme} */
  const theme = useTheme();
  const isTooSmall = useMediaQuery(theme.breakpoints.down('xl'));

  const notificationCount = currentUser
    ? currentUserNotificationCount.provider + currentUserNotificationCount.customer
    : 0;

  const isAuthenticatedOrJustHydrated = isAuthenticated;

  const showButtonLinks = intl.locale === config.locale; // Show only for German users

  const [location, setLocation] = useState(
    typeof window !== 'undefined' && window.currentLocation
      ? {
          origin: window.currentLocation,
          bounds: locationBounds(
            window.currentLocation,
            config.maps.search.currentLocationBoundsDistance
          ),
        }
      : defaultSearchCountry(intl.locale)
  );

  useEffect(() => {
    let isMounted = true;
    if (typeof window !== 'undefined' && window.currentLocation) {
      const bounds = locationBounds(
        window.currentLocation,
        config.maps.search.currentLocationBoundsDistance
      );
      if (bounds) {
        setLocation({
          origin: window.currentLocation,
          bounds,
        });
      }
    } else {
      // Only a prefill: never open the permission prompt for it on page load.
      hasLocationPermission()
        .then((granted) => (granted ? userLocation() : null))
        .then((latLng) => {
          if (isMounted && latLng) {
            const bounds = locationBounds(latLng, config.maps.search.currentLocationBoundsDistance);
            if (bounds) {
              setLocation({
                origin: latLng,
                bounds,
              });
            }
          }
        })
        .catch(() => null);
    }
    return () => {
      isMounted = false;
    };
  }, []);

  const renderButtons = useMemo(() => {
    // Left-side buttons configuration
    const buttonsConfig = [
      {
        key: 'spotsGroup',
        type: 'menu',
        show: true,
        messageId: 'TopbarDesktop.spotsGroup',
        items: [
          {
            key: 'camperVan',
            to: { name: 'StoryblokPage', params: { slug: 'wohnmobilstellplatz' } },
            messageId: 'TopbarDesktop.toCamperVanSpots',
          },
          {
            key: 'tent',
            to: { name: 'StoryblokPage', params: { slug: 'zeltplatz' } },
            messageId: 'TopbarDesktop.toCampsitesPage',
          },
          {
            key: 'caravan',
            to: { name: 'StoryblokPage', params: { slug: 'wohnwagenstellplatz' } },
            messageId: 'TopbarDesktop.toCaravanVanSpots',
          },
        ],
      },
      {
        key: 'pass',
        show: true,
        to: { name: 'StoryblokPage', params: { slug: 'pass' } },
        messageId: 'TopBarDesktop.annualPass',
        color: 'secondary',
        variant: 'text',
        ariaLabel: 'open vansite pass',
      },
      {
        key: 'app',
        show: true,
        to: { name: 'StoryblokPage', params: { slug: 'stellplatz-app' } },
        messageId: 'TopbarDesktop.toAppPage',
        color: 'secondary',
        variant: 'text',
        ariaLabel: 'open app page',
      },
      {
        key: 'shop',
        show: true,
        to: 'https://shop.vansite.eu/',
        messageId: 'TopBarDesktop.toShopPage',
        color: 'secondary',
        variant: 'text',
        ariaLabel: 'open shop page',
      },
      {
        key: 'guide',
        show: true,
        to: { name: 'StoryblokPage', params: { slug: 'stellplatzfuehrer' } },
        messageId: 'TopbarDesktop.toGuidePage',
        color: 'secondary',
        variant: 'text',
        ariaLabel: 'open guide page',
      },
      {
        key: 'insuranceLetter',
        show: showButtonLinks,
        to: 'https://shop.vansite.eu/collections/camping-geschenk-ideen',
        messageId: 'TopbarDesktop.toInsuranceLetterPage',
        color: 'secondary',
        variant: 'text',
        ariaLabel: 'open insurance letter page',
      },
      {
        key: 'dumpStations',
        show: true,
        to: { name: 'DumpStationSearchPage', search: { ...location } },
        messageId: 'TopbarDesktop.toDumpStationsPage',
        color: 'secondary',
        variant: 'text',
        ariaLabel: 'open dump stations page',
      },
      {
        key: 'routePlanner',
        show: true,
        to: { name: 'RoutePlannerPage' },
        messageId: 'TopbarDesktop.toRoutePlannerPage',
        color: 'secondary',
        variant: 'text',
        ariaLabel: 'open route planner page',
        beta: true,
      },
    ];

    return buttonsConfig
      .filter(({ show }) => show)
      .map(({ key, type, ...buttonProps }) => {
        if (type === 'menu') {
          return <TopBarMenuButton key={key} {...buttonProps} />;
        }
        return <TopBarButton key={key} {...buttonProps} />;
      });
  }, [showButtonLinks, location]);

  const rightSideButtonsConfig = useMemo(() => {
    return [
      {
        key: 'newListing',
        show: !isTooSmall,
        to: isAuthenticated
          ? { name: 'NewListingPage' }
          : {
              name: 'SignupPage',
              state: {
                from: pathByRouteName('NewListingPage', routeConfiguration(), {}, intl.locale),
              },
            },
        messageId: isAuthenticated ? 'TopbarDesktop.createListing' : 'TopbarDesktop.becomeHost',
        ariaLabel: isAuthenticated ? 'create new listing' : 'go to sign up page',
        color: 'secondary',
        variant: 'text',
      },
    ];
  }, [isTooSmall, isAuthenticated, intl.locale]);

  const rightSideButtons = useMemo(() => {
    return rightSideButtonsConfig
      .filter(({ show }) => show)
      .map(({ key, ...buttonProps }) => <TopBarButton key={key} {...buttonProps} />);
  }, [rightSideButtonsConfig]);

  const logoLink = (
    <NamedLink ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.ariaLabel' })} name="LandingPage">
      <Logo format="desktop" sx={logoStyle} />
    </NamedLink>
  );

  return (
    <AppBar
      position="static"
      sx={{
        bgcolor: theme.palette.background.default,
        borderBottom: `1px solid ${neutral[200]}`,
        boxShadow: 0,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          height: `${theme.globals.topBarHeightDesktop}px`,
          width: '100%',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', height: 1 }}>
          <Stack alignItems="center" direction="row" spacing={2}>
            {logoLink}
            <Stack direction="row" sx={navLinksSx}>
              {renderButtons}
            </Stack>
          </Stack>
          <Stack alignItems="center" direction="row" spacing={2}>
            {rightSideButtons}
            <LanguageMenu />
            {isAuthenticated ? (
              <ProfileMenu
                currentPage={currentPage}
                currentUser={currentUser}
                currentUserHasListings={currentUserHasListings}
                notificationCount={notificationCount}
                onLogout={onLogout}
              />
            ) : (
              <LoginMenu
                isAuthenticatedOrJustHydrated={isAuthenticatedOrJustHydrated}
                locale={intl.locale}
              />
            )}
          </Stack>
        </Toolbar>
      </Box>
    </AppBar>
  );
};

TopbarDesktopComponent.displayName = 'TopbarDesktop';
const TopbarDesktop = memo(TopbarDesktopComponent);

export default TopbarDesktop;
